import angular from 'angular';
import appCache from './ApplicationCache';
import globalMessageServiceModule from './GlobalMessageService';
import { taskClassForType } from '../tasks';
import { createError } from '../error';

const taskQueueKeys = {
  pending: 'tasks_pending',
  executing: 'tasks_executing',
  failed: 'tasks_failed',
};
const lastTaskIdKey = 'last_task_id';

const TASK_STATUS_QUEUED = 0;
const TASK_STATUS_EXECUTING = 1;
const TASK_STATUS_COMPLETED = 2;
const TASK_STATUS_ERROR = 3;

class TaskQueueService {
  constructor(
    $q,
    $timeout,
    $log,
    $injector,
    ApplicationCache,
    LogService,
    GlobalMessageService
  ) {
    this.$q = $q;
    this.$timeout = $timeout;
    this.$log = $log;
    this.$injector = $injector;
    this.ApplicationCache = ApplicationCache;
    this.LogService = LogService;
    this.GlobalMessageService = GlobalMessageService;

    this.taskQueues = {};
    this.initialize();

    this.currentTask = null;
    this.currentTaskPromise = null;

    this._executingTaskPromises = [];

    this._objectIdsWithEnqueuedUpdatesByType = {};
  }

  get pendingTasks() {
    return this.taskQueues.pending;
  }

  get failedTasks() {
    return this.taskQueues.failed;
  }

  get executingTasks() {
    return this.taskQueues.executing;
  }

  initialize() {
    const promises = Object.keys(taskQueueKeys)
      .map(key => {
        return this.ApplicationCache.getItem(taskQueueKeys[key])
          .then(queue => {
            if (queue === null) {
              queue = [];
            }
            this.taskQueues[key] = queue;
          })
      });
    return this.$q.all(promises);
  }

  persist() {
    const promises = Object.keys(taskQueueKeys)
      .map(key => {
        return this.ApplicationCache.setItem(taskQueueKeys[key], this.taskQueues[key]);
      });
    return this.$q.all(promises);
  }

  attemptToPersist() {
    return this.persist()
      .catch(err => {
        this.LogService.error("Swallowing error during persist():", err);
        return null;
      })
  }

  createTask(type) {
    const cls = taskClassForType(type);
    return this.$injector.instantiate(cls);
  }

  restoreTask(taskInfo) {
    const { type, data } = taskInfo;
    const task = this.createTask(type);
    return this.$q.when(task.populate(data))
      .then(() => task);
  }

  serializeTask(task) {
    return this.$q.when(task.serialize())
      .then(data => {
        return {
          type: task.constructor.name,
          data,
        };
      });
  }

  executeOrEnqueue(task) {
    if (task.canExecute) {
      return task.execute();
    }
    return this.enqueue(task);
  }

  enqueue(task) {
    return this.$q.when(this.serializeTask(task))
      .then(taskInfo => {
        this.taskQueues.pending.push(taskInfo);
        this.GlobalMessageService.addMessage("Enqueued task");

        // Mark object ID's that are updating
        task.updatingObjectTypesAndIds.forEach(objectTypeAndId => {
          let [objectType, objectId] = objectTypeAndId;
          this.updateEnqueudForObjectId(objectId, objectType, task);
        });

        return this.attemptToPersist();
      });
  }

  executeAllTasks() {
    let numExecuted = 0;
    if (this.pendingTasks.length === 0) {
      return this.$q.when(numExecuted);
    }
    return this.executeNextTask()
      .then(() => {
        numExecuted += 1;
        return numExecuted;
      })
      .finally(() => {
        return this.executeAllTasks()
          .then(n => {
            numExecuted += n;
            return numExecuted;
          })
      });
  }

  executeNextTask() {
    const nextTaskInfo = this.taskQueues.pending.shift();
    if (!nextTaskInfo) {
      this.LogService.debug("No task left in queue");
      return this.$q.reject(createError({
        error: 'NoTasksInQueue',
        message: 'No tasks left in the task queue',
      }));
    }
    return this.attemptToPersist()
      .then(() => this.executeTask(nextTaskInfo));
  }

  executeTask(taskInfo) {
    this.GlobalMessageService.addMessage("Processing task in background");
    this._taskExecuting(taskInfo);
    const promise = this.restoreTask(taskInfo)
      .catch(err => {
        this.LogService.error("Error restoring task with info:", taskInfo);
        return this.$q.reject(err);
      })
      .then(task => {
        return task.execute()
          .then(result => {
            this.GlobalMessageService.addMessage("Task completed successfully: " + this.LogService.stringify(taskInfo));
            return result;
          })
          .catch(err => {
            this.LogService.error("Error executing task! Task info:", taskInfo, "task:", task, "error:", err);
            this.GlobalMessageService.addError(err);
            this._taskErrored(task, taskInfo, err);
            return this.$q.reject(err);
          })
          .finally(result => {
            this._taskFinishedExecuting(task, taskInfo, promise);
            return result;
          });
      })
      .finally(() => this.attemptToPersist());
    this._executingTaskPromises.push(promise);
    return promise;
  }

  updateEnqueudForObjectId(id, type, task = null) {
    if (this._objectIdsWithEnqueuedUpdatesByType[type] === undefined) {
      this._objectIdsWithEnqueuedUpdatesByType[type] = {};
    }
    if (!task) {
      task = true;
    }
    this._objectIdsWithEnqueuedUpdatesByType[type][id] = task;
  }

  isUpdateEnqueuedForObjectId(id, type) {
    return this._objectIdsWithEnqueuedUpdatesByType[type] && this._objectIdsWithEnqueuedUpdatesByType[type][id];
  }

  clearUpdateEnqueuedForObjectId(id, type) {
    if (this._objectIdsWithEnqueuedUpdatesByType[type] && this._objectIdsWithEnqueuedUpdatesByType[type][id]) {
      delete this._objectIdsWithEnqueuedUpdatesByType[type][id];
    }
  }

  clearAllUpdateEnqueuedForType(type) {
    this._objectIdsWithEnqueuedUpdatesByType[type] = {};
  }

  clearAllUpdateEnqueued() {
    this._objectIdsWithEnqueuedUpdatesByType = {};
  }

  _taskExecuting(taskInfo) {
    this.taskQueues.executing.push(taskInfo);
  }

  _taskFinishedExecuting(task, taskInfo, taskPromise) {
    const taskIdx = this.taskQueues.executing.indexOf(taskInfo);
    if (taskIdx !== -1) {
      this.taskQueues.executing.splice(taskIdx, 1);
    } else {
      this.LogService.warn("Unable to find task info in executing tasks. taskInfo:", taskInfo, "executing tasks:", this.taskQueues.executing);
    }
    const promiseIdx = this._executingTaskPromises.indexOf(taskPromise);
    if (promiseIdx !== -1) {
      this._executingTaskPromises.splice(promiseIdx, 1);
    }

    // Mark object ID's that are updating
    task.updatingObjectTypesAndIds.forEach(objectTypeAndId => {
      let [objectType, objectId] = objectTypeAndId;
      this.clearUpdateEnqueuedForObjectId(objectId, objectType);
    });
  }

  _taskErrored(task, taskInfo, error) {
    // Attempt to get error message
    let serializableError;
    try {
      serializableError = JSON.parse(this.LogService.stringify(error));
    } catch (e) {
      this.LogService.error("Error serializing error message for task", taskInfo, "error:", error);
    }
    taskInfo.error = serializableError;
    this.taskQueues.failed.push(taskInfo);
  }
}

const moduleName = 'recaps.services.TaskQueueService';
let module = angular.module(moduleName, [
  appCache.name,
  globalMessageServiceModule.name,
]);

module.service('TaskQueueService', TaskQueueService)
  .config($localForageProvider => {
    $localForageProvider.config({
      name: 'staff_application_cache',
    })
  });

export default module;
